/**
 * Thin QGS API helper that always targets the Django backend on :8000
 * (bypassing MSW even when the PWA runs in mockup mode). Handles CSRF the
 * same way apiClient does.
 */
const API_BASE = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
let csrfToken = null;


/**
 * Fetch a real 64-char Django CSRF token from :8000/csrf-token/. The MSW
 * handler for this URL is scoped to :3000 only (see mocks/handlers.js), so
 * this cross-origin fetch reaches Django unmediated. ``credentials:
 * 'include'`` also brings the ``csrftoken`` cookie back on subsequent POSTs
 * so Django's CSRF middleware can compare the two.
 */
async function getCsrf() {
  if (csrfToken) return csrfToken;
  try {
    const r = await fetch(`${API_BASE}/api/frontend/auth/csrf-token/`, {
      credentials: 'include',
    });
    if (r.ok) {
      const b = await r.json();
      // Server shape: { success: true, data: { csrfToken: '...' } }
      csrfToken = b?.data?.csrfToken || null;
    }
  } catch { /* offline — POST will 403 and surface to the caller */ }
  return csrfToken;
}


async function req(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (method !== 'GET' && method !== 'HEAD') {
    const t = await getCsrf();
    if (t) headers['X-CSRFToken'] = t;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method, headers, credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    // Preserve the status + parsed body on the thrown error so callers
    // (e.g. KoApprovalModal) can distinguish 401 vs 403 vs 423 and read
    // {attempts_left, locked_now, error} without re-parsing.
    let parsedBody = null;
    try { parsedBody = await res.json(); } catch {}
    const message = (parsedBody && (parsedBody.error || parsedBody.detail))
      || `${method} ${path} → ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.body = parsedBody || {};
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}


/** Create the audit envelope. Returns the created Submission row. */
export function createSubmission(payload) {
  return req('/api/frontend/qgs/submissions/', { method: 'POST', body: payload });
}


/** Mark a Submission SUBMITTED. Idempotent. */
export function completeSubmission(submissionId) {
  return req(`/api/frontend/qgs/submissions/${submissionId}/complete/`, { method: 'POST' });
}


/** Fetch a Submission with fresh knockouts_count / reworks_count. */
export function getSubmission(submissionId) {
  return req(`/api/frontend/qgs/submissions/${submissionId}/`);
}


/**
 * POST one KO row. Photo is a Cloudinary URL string.
 * Response body carries the KO plus a ``submission_summary`` block —
 * see submission_views.KnockoutViewSet.create.
 */
export function createKnockout(payload) {
  return req('/api/frontend/qgs/knockouts/', { method: 'POST', body: payload });
}


/**
 * QGS v2 · POST one KO row with a Line Leader signature. The backend
 * verifies approver_username + pin, and if OK commits the row already
 * OPEN + APPROVED in a single transaction. Response carries the KO,
 * a submission_summary and an approver block.
 *
 * On failure the thrown Error has .status and .body populated so the
 * approval modal can distinguish wrong-pin (401 + attempts_left), not-
 * authorised (403), and locked-out (423).
 */
export function createKnockoutWithApproval(payload) {
  return req('/api/frontend/qgs/knockouts/create-with-approval/',
             { method: 'POST', body: payload });
}


/** POST one Rework row. Same shape as createKnockout. */
export function createRework(payload) {
  return req('/api/frontend/qgs/rework-records/', { method: 'POST', body: payload });
}
